// ─── theme.js ────────────────────────────────────────────────────────────────
// Single source of truth for all design tokens.
// Import `theme` into any component for colors, spacing, radii, shadows, etc.
// CSS variables are also injected globally via `injectTheme()`.
// ─────────────────────────────────────────────────────────────────────────────

export const theme = {
  // ── Colors ─────────────────────────────────────────────────────────────────
  color: {
    // Backgrounds
    bgBase: "#0d0d12",
    bgSurface: "#111118",
    bgCard: "rgba(255,255,255,0.03)",
    bgCardHover: "rgba(255,255,255,0.06)",
    bgNav: "rgba(14,14,21,0.92)",
    bgStats: "rgba(255,255,255,0.04)",
    bgPill: "rgba(249,115,22,0.10)",
    bgPillHover: "rgba(249,115,22,0.20)",
    bgBetPill: "rgba(255,255,255,0.05)",

    // Brand / Accent
    accent: "#f97316", // orange-500
    accentLight: "#fb923c", // orange-400
    accentDark: "#d97706", // amber-600
    accentGlow: "rgba(249,115,22,0.55)",
    accentGlowHi: "rgba(249,115,22,0.75)",
    accentGlowRg: "rgba(249,115,22,0.20)",
    accentRing: "rgba(249,115,22,0.30)",
    accentMuted: "rgba(249,115,22,0.10)",

    // Text
    textPrimary: "#ffffff",
    textSub: "rgba(255,255,255,0.90)",
    textMuted: "rgba(255,255,255,0.50)",
    textDim: "rgba(255,255,255,0.35)",
    textFaint: "rgba(255,255,255,0.30)",
    textAccent: "#fb923c",
    textAccentDim: "rgba(251,146,60,0.70)",

    // Borders
    border: "rgba(255,255,255,0.07)",
    borderCard: "rgba(255,255,255,0.06)",
    borderHover: "rgba(249,115,22,0.20)",
    borderAccent: "rgba(249,115,22,0.40)",
    borderDivide: "rgba(255,255,255,0.10)",

    // Rank dots
    rankGold: "#facc15",
    rankSilver: "#cbd5e1",
    rankBronze: "#d97706",
    rankDefault: "#52525b",

    // Play button gradient stops
    playFrom: "#fb923c",
    playVia: "#f97316",
    playTo: "#d97706",

    // Coin badge
    coinBg: "#fbbf24",
    coinFg: "#0d0d12",

    // Status bar
    statusText: "rgba(255,255,255,0.40)",
  },

  // ── Typography ──────────────────────────────────────────────────────────────
  font: {
    family: "'Barlow Condensed', 'Oswald', sans-serif",
    bodyFamily: "'DM Sans', 'Nunito', sans-serif",
    weightBlack: 900,
    weightBold: 700,
    weightSemi: 600,
    weightMed: 500,
    weightReg: 400,
  },

  // ── Spacing ─────────────────────────────────────────────────────────────────
  space: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },

  // ── Radii ───────────────────────────────────────────────────────────────────
  radius: {
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
    xxl: "40px",
    full: "9999px",
  },

  // ── Shadows ─────────────────────────────────────────────────────────────────
  shadow: {
    phone: "0 40px 120px rgba(0,0,0,0.9)",
    playBtn: "0 0 60px rgba(249,115,22,0.55)",
    playHov: "0 0 90px rgba(249,115,22,0.75)",
    logo: "0 0 16px rgba(249,115,22,0.50)",
    glowBar: "0 0 8px rgba(249,115,22,0.80)",
    coin: "0 2px 8px rgba(0,0,0,0.6)",
    rankGold: "0 0 6px rgba(250,204,21,0.60)",
    rankSilv: "0 0 6px rgba(203,213,225,0.60)",
    rankBrnz: "0 0 6px rgba(217,119,6,0.60)",
    icon: "drop-shadow(0 0 6px rgba(249,115,22,0.80))",
  },

  // ── Transitions ─────────────────────────────────────────────────────────────
  transition: {
    fast: "all 0.15s ease",
    base: "all 0.20s ease",
    slow: "all 0.30s ease",
  },

  // ── Z-index ─────────────────────────────────────────────────────────────────
  z: {
    base: 1,
    above: 10,
    top: 20,
  },
};

// ── Inject CSS variables + Google Fonts into <head> ──────────────────────────
export function injectTheme() {
  if (document.getElementById("glow-bingo-theme")) return;

  // Google Fonts
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;900&family=DM+Sans:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);

  // CSS Variables
  const style = document.createElement("style");
  style.id = "glow-bingo-theme";
  style.textContent = `
    :root {
      --color-bg-base:       ${theme.color.bgBase};
      --color-bg-surface:    ${theme.color.bgSurface};
      --color-bg-card:       ${theme.color.bgCard};
      --color-bg-card-hover: ${theme.color.bgCardHover};
      --color-bg-nav:        ${theme.color.bgNav};
      --color-bg-stats:      ${theme.color.bgStats};
      --color-bg-pill:       ${theme.color.bgPill};
      --color-bg-pill-hover: ${theme.color.bgPillHover};
      --color-bg-bet-pill:   ${theme.color.bgBetPill};

      --color-accent:        ${theme.color.accent};
      --color-accent-light:  ${theme.color.accentLight};
      --color-accent-dark:   ${theme.color.accentDark};
      --color-accent-glow:   ${theme.color.accentGlow};
      --color-accent-ring:   ${theme.color.accentRing};
      --color-accent-muted:  ${theme.color.accentMuted};

      --color-text-primary:  ${theme.color.textPrimary};
      --color-text-sub:      ${theme.color.textSub};
      --color-text-muted:    ${theme.color.textMuted};
      --color-text-dim:      ${theme.color.textDim};
      --color-text-faint:    ${theme.color.textFaint};
      --color-text-accent:   ${theme.color.textAccent};

      --color-border:        ${theme.color.border};
      --color-border-card:   ${theme.color.borderCard};
      --color-border-hover:  ${theme.color.borderHover};
      --color-border-accent: ${theme.color.borderAccent};
      --color-border-divide: ${theme.color.borderDivide};

      --font-display:        ${theme.font.family};
      --font-body:           ${theme.font.bodyFamily};

      --radius-sm:           ${theme.radius.sm};
      --radius-md:           ${theme.radius.md};
      --radius-lg:           ${theme.radius.lg};
      --radius-xl:           ${theme.radius.xl};
      --radius-xxl:          ${theme.radius.xxl};
      --radius-full:         ${theme.radius.full};

      --shadow-phone:        ${theme.shadow.phone};
      --shadow-play:         ${theme.shadow.playBtn};
      --shadow-play-hov:     ${theme.shadow.playHov};
      --shadow-logo:         ${theme.shadow.logo};
      --shadow-glow-bar:     ${theme.shadow.glowBar};
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      background: var(--color-bg-base);
      font-family: var(--font-body);
      -webkit-font-smoothing: antialiased;
    }

    /* ── Keyframe animations ── */
    @keyframes ping {
      0%   { transform: scale(1);   opacity: 0.6; }
      80%  { transform: scale(1.8); opacity: 0;   }
      100% { transform: scale(1.8); opacity: 0;   }
    }
    @keyframes ping-slow {
      0%   { transform: scale(1);   opacity: 0.4; }
      80%  { transform: scale(2.2); opacity: 0;   }
      100% { transform: scale(2.2); opacity: 0;   }
    }
    @keyframes pulse-text {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.4; }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .ping-ring-1 {
      animation: ping      2.0s ease-out infinite;
    }
    .ping-ring-2 {
      animation: ping-slow 2.4s ease-out infinite;
      animation-delay: 0.3s;
    }
    .pulse-text {
      animation: pulse-text 1.2s ease-in-out infinite;
    }
    .fade-in-up {
      animation: fadeInUp 0.35s ease both;
    }

    /* ── Play button hover ── */
    .play-btn {
      transition: ${theme.transition.slow};
    }
    .play-btn:hover {
      transform: scale(1.06);
      box-shadow: ${theme.shadow.playHov};
    }
    .play-btn:active {
      transform: scale(0.95);
    }

    /* ── Winner row hover ── */
    .winner-row {
      transition: ${theme.transition.base};
    }
    .winner-row:hover {
      background: var(--color-bg-card-hover);
      border-color: var(--color-border-hover);
    }

    /* ── Nav tab hover ── */
    .nav-tab {
      transition: ${theme.transition.base};
      cursor: pointer;
      border: none;
      background: none;
    }
    .nav-tab:hover .nav-icon,
    .nav-tab:hover .nav-label {
      color: rgba(255,255,255,0.60);
    }

    /* ── Game rules button hover ── */
    .rules-btn {
      transition: ${theme.transition.base};
    }
    .rules-btn:hover {
      background: var(--color-bg-pill-hover);
    }

    /* ── See all button hover ── */
    .see-all-btn {
      transition: ${theme.transition.base};
      background: none;
      border: none;
      cursor: pointer;
    }
    .see-all-btn:hover {
      color: var(--color-text-accent);
    }
  `;
  document.head.appendChild(style);
}
